import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { BaseRestService } from "./base-rest.service";
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class LoanService extends BaseRestService {

  readonly API_URL: string = environment.loanUri + '/loans';

  getList(pageParams: any): Observable<any> {
    const options = {
      headers: this.headers,
      params: this.removeEmptyPropertyFromObject(pageParams)
    }
    return this.httpClient.get(this.API_URL, options);
  }

  queueSummaryExport(data: any, format: 'XLSX' | 'CSV' = 'XLSX'): Observable<string> {
    return this.httpClient.post(this.API_URL + '/summary/export-requests', JSON.stringify(data), {
      headers: this.headers,
      params: {format},
      responseType: 'text'
    });
  }
  getSummaryExports(params: any): Observable<any> { return this.httpClient.get(this.API_URL + '/summary/export-requests', {headers: this.headers, params}); }
  downloadSummaryExport(id: string): Observable<any> { return this.httpClient.get(this.API_URL + '/summary/export-requests/' + id + '/download', {headers: this.headers, responseType: 'blob'}); }
  deleteSummaryExport(id: string): Observable<any> { return this.httpClient.delete(this.API_URL + '/summary/export-requests/' + id, {headers: this.headers}); }

  queueOperationSummaryExport(data: any, format: 'XLSX' | 'CSV'): Observable<string> {
    return this.httpClient.post(this.API_URL + '/operation/summary/export-requests', JSON.stringify(data), {
      headers: this.headers,
      params: {format},
      responseType: 'text'
    });
  }
  getOperationSummaryExports(params: any): Observable<any> { return this.httpClient.get(this.API_URL + '/operation/summary/export-requests', {headers: this.headers, params}); }
  downloadOperationSummaryExport(id: string): Observable<Blob> { return this.httpClient.get(this.API_URL + '/operation/summary/export-requests/' + id + '/download', {headers: this.headers, responseType: 'blob'}); }
  deleteOperationSummaryExport(id: string): Observable<any> { return this.httpClient.delete(this.API_URL + '/operation/summary/export-requests/' + id, {headers: this.headers}); }

  queueClassificationSummaryExport(data: any): Observable<string> {
    return this.httpClient.post(this.API_URL + '/risk/classification/export-requests', JSON.stringify(data), {
      headers: this.headers,
      responseType: 'text'
    });
  }
  getClassificationSummaryExports(params: any): Observable<any> { return this.httpClient.get(this.API_URL + '/risk/classification/export-requests', {headers: this.headers, params}); }
  downloadClassificationSummaryExport(id: string): Observable<Blob> { return this.httpClient.get(this.API_URL + '/risk/classification/export-requests/' + id + '/download', {headers: this.headers, responseType: 'blob'}); }
  deleteClassificationSummaryExport(id: string): Observable<any> { return this.httpClient.delete(this.API_URL + '/risk/classification/export-requests/' + id, {headers: this.headers}); }

  getListByCustomer(customerId, pageParams: any): Observable<any> {
    const options = {
      headers: this.headers,
      params: this.removeEmptyPropertyFromObject(pageParams)
    }
    return this.httpClient.get(this.API_URL + "/customers/" + customerId, options);
  }

  getListLoanPartialByCustomer(customerId, pageParams: any, query?: string): Observable<any> {
    const options = {
      headers: this.headers,
      params: this.removeEmptyPropertyFromObject(pageParams)
    }
    return this.httpClient.get(this.API_URL + "/customers/" + customerId + "/partial?" + query, options);
  }

  getScheduleReport(id: string, customerId: string, type: string='pdf'): Observable<any> {
    return this.httpClient.get(`${this.API_URL}/${id}/customers/${customerId}/schedule/report?type=${type}`,
      { headers: this.headers, responseType: 'arraybuffer' });
  }

  getSchedule(id: string): Observable<any> {
    return this.httpClient.get(`${this.API_URL}/${id}/schedule`,
      { headers: this.headers });
  }

  getLoanIdByGrantingAccount(accountId: string): Observable<any> {
    return this.httpClient.get(`${this.API_URL}/granting-accounts/${accountId}`,
      { headers: this.headers });
  }

  getLoanByGrantingAccounts(data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/granting-accounts", JSON.stringify(data),
      { headers: this.headers });
  }

  getById(id: string): Observable<any> {
    return this.httpClient.get(this.API_URL + "/" + id,
      { headers: this.headers });
  }

  create(data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/personal", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  createGroupLoan(data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/personal", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  createCustomerGroupLoan(data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/personal/list", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  update(id: string, data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/personal/" + id, JSON.stringify(data),
      { headers: this.headers });
  }

  delete(id: string): Observable<any> {
    return this.httpClient.delete(this.API_URL + "/" + id, 
      { headers: this.headers, responseType: 'text' });
  }

  repayment(id, data): Observable<any> {
    return this.httpClient.post(`${this.API_URL}/${id}/pre-repay`, JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  repaymentSummary(id): Observable<any> {
    return this.httpClient.get(`${this.API_URL}/${id}/repayment/summary`,
      { headers: this.headers, });
  }

  repaymentSummaryFull(id): Observable<any> {
    return this.httpClient.get(`${this.API_URL}/${id}/repayment/summary/full`,
      { headers: this.headers, });
  }

  changeStatus(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/personal/status", JSON.stringify(data),
      { headers: this.headers });
  }

  getLoanByStatus(status, pageParams: any): Observable<any> {
    const options = {
      headers: this.headers,
      params: this.removeEmptyPropertyFromObject(pageParams)
    }
    return this.httpClient.get(this.API_URL + "/status/" + status, options);
  }

  upfrontFeeCharge(id, data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/" + id + "/upfront-fee/charge", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  upfrontServiceFeeCharge(id, data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/" + id + "/service-upfront-fee/charge", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  upfrontVatCharge(id, data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/" + id + "/upfront-vat/charge", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  upfrontStampDutyCharge(id, data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/" + id + "/upfront-stamp-duty/charge", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  upfrontDisasterRecoveryCharge(id, data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/" + id + "/upfront-disaster-recovery/charge", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  getUpfrontFeeCharge(id): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/upfront-fee/charge", options);
  }

  getUpfrontFeeChargeInfo(id, reserveDocumentNumber: boolean = true): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/upfront-fee/charging/info?reserve-document-number=" + reserveDocumentNumber, options);
  }

  getUpfrontServiceFeeChargeInfo(id, reserveDocumentNumber: boolean = true): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/service-upfront-fee/charging/info?reserve-document-number=" + reserveDocumentNumber, options);
  }

  getUpfrontVatChargeInfo(id, reserveDocumentNumber: boolean = true): Observable<any> {
    return this.httpClient.get(this.API_URL + "/" + id + "/upfront-vat/charging/info?reserve-document-number=" + reserveDocumentNumber, { headers: this.headers });
  }

  getUpfrontStampDutyChargeInfo(id, reserveDocumentNumber: boolean = true): Observable<any> {
    return this.httpClient.get(this.API_URL + "/" + id + "/upfront-stamp-duty/charging/info?reserve-document-number=" + reserveDocumentNumber, { headers: this.headers });
  }

  getUpfrontDisasterRecoveryChargeInfo(id, reserveDocumentNumber: boolean = true): Observable<any> {
    return this.httpClient.get(this.API_URL + "/" + id + "/upfront-disaster-recovery/charging/info?reserve-document-number=" + reserveDocumentNumber, { headers: this.headers });
  }

  getByGrantingAccountId(id): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/granting-accounts/" + id, options);
  }

  getByGrantingAccountIds(data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/granting-accounts", JSON.stringify(data),
      { headers: this.headers });
  }

  getGrantingDocumentNumber(): Observable<string> {
    return this.httpClient.get(this.API_URL + "/granting/document-number", { headers: this.headers, responseType: 'text' });
  }

  getUpfrontFeeChargingDocumentNumber(): Observable<string> {
    return this.httpClient.get(this.API_URL + "/upfront-fee/charging/document-number", { headers: this.headers, responseType: 'text' });
  }

  getRepaymentDocumentNumber(): Observable<string> {
    return this.httpClient.get(this.API_URL + "/repayment/document-number", { headers: this.headers, responseType: 'text' });
  }

  grantLoan(id, data): Observable<string> {
    return this.httpClient.post(this.API_URL + "/" + id + "/grant", JSON.stringify(data),
      { headers: this.headers, responseType: 'text' });
  }

  getGrantingInfo(id): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/granting/info",options);
  }

  calculate(id, data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/" + id + "/calculate", JSON.stringify(data),
    { headers: this.headers });
  }

  getCalculate(id, nowDate): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/calculation/info?now-date=" + nowDate, options);
  }

  getPrepaidChargesReturnInfo(id: string): Observable<any> {
    return this.httpClient.get(this.API_URL + "/" + id + "/prepaid-charges/return/info", { headers: this.headers });
  }
  returnPrepaidCharges(id: string, request: unknown): Observable<any> {
    return this.httpClient.post(this.API_URL + "/" + id + "/prepaid-charges/return", JSON.stringify(request), { headers: this.headers });
  }

  repay(id, data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/" + id + "/repay", JSON.stringify(data),
    { headers: this.headers });
  }

  getRepaymentSummary(id, nowDate, nowDate0): Observable<any> {
    const options = {
      headers: this.headers,
    }

    let parameters = '';
    if (nowDate) {
      parameters = 'now-date=' + nowDate;
    }

    if (nowDate0) {
      if (parameters) {
        parameters += '&';
      }
      parameters += 'now-date0=' + nowDate0;
    }
    if (parameters) {
      return this.httpClient.get(this.API_URL + "/" + id + "/repayment/summary?" + parameters, options);
    } else {
      return this.httpClient.get(this.API_URL + "/" + id + "/repayment/summary", options);
    }
  }

  loanApprove(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/approve", JSON.stringify(data),
        { headers: this.headers });
  }

  loanReject(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/reject", JSON.stringify(data),
        { headers: this.headers });
  }

  loanSendForApproval(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/send/for-approval", JSON.stringify(data),
        { headers: this.headers });
  }

  loanSendForRepairing(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/send/for-repairing", JSON.stringify(data),
        { headers: this.headers });
  }

  cancel(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/personal/cancel", JSON.stringify({...data, channelCode: 'OPERATOR'}),
        { headers: this.headers });
  }

  close(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/personal/close", JSON.stringify(data),
        { headers: this.headers });
  }

  reopen(data): Observable<any> {
    return this.httpClient.put(this.API_URL + "/personal/reopen", JSON.stringify(data),
        { headers: this.headers });
  }

  adjust(id, data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/" + id + "/adjust", JSON.stringify(data),
    { headers: this.headers });
  }

  getAdjust(id): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/adjustment/info", options);
  }

  getOperationSummary(pageParams, data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/operation/summary", data,
      { headers: this.headers, params: this.removeEmptyPropertyFromObject(pageParams) });
  }

  getLoansSummary(pageParams, data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/summary", data,
      { headers: this.headers, params: this.removeEmptyPropertyFromObject(pageParams) });
  }

  downloadLoansSummary(data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/summary/report", data,
      { headers: this.headers, responseType: 'arraybuffer' });
  }

  downloadOperationSummary(data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/operation/summary/report", data,
      { headers: this.headers, responseType: 'arraybuffer' });
  }

  deleteLoanOperationSummary(data): Observable<any> {
    return this.httpClient.delete(this.API_URL + "/operations",
      { headers: this.headers, body: data });
  }

  getClassificationByLoan(id, date): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/classification/info?now-date=" +date, options);
  }

  createClassification(id, data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/" + id + "/classify", JSON.stringify(data),
        { headers: this.headers });
  }

  getProvisioningByLoan(id, date): Observable<any> {
    const options = {
      headers: this.headers,
    }
    return this.httpClient.get(this.API_URL + "/" + id + "/provisioning/info?now-date=" +date, options);
  }

  makeProvisioning(id, data): Observable<any> {
    return this.httpClient.post(this.API_URL + "/" + id + "/make-provisioning", JSON.stringify(data),
        { headers: this.headers });
  }

  getAllClassificationOperationSummary(data: any, pageParams?: any): Observable<any> {
    const options: any = { headers: this.headers };
    if (pageParams) {
      options.params = this.removeEmptyPropertyFromObject(pageParams);
    }
    return this.httpClient.post(this.API_URL + "/risk/classification", data, options);
  }

  addFile(data, id): Observable<any> {
    let headers = new HttpHeaders();
    headers.append("Content-Type", " multipart/form-data");
    return this.httpClient.post(this.API_URL + "/" + id + "/documents", data, {
        headers
    });
  }

  deleteFiles(data, id): Observable<any> {
    let headers = new HttpHeaders();
    headers.append("Content-Type", " multipart/form-data");
    return this.httpClient.delete(this.API_URL + "/" + id + "/documents", {
        headers
    });
  }

  getFileById(fileId: string, id): any {
    let headers = new HttpHeaders();
    headers.append("Content-Type", "application/octet-stream");
    return this.httpClient.get(this.API_URL + "/" + id + `/documents/${fileId}`,
        { headers: this.headers, responseType: 'blob' });
  }

}
